<template>
  <div class="modals-container">
    <VModal v-if="!userState.user && authError" v-model:showing="authError" :hide-close="hideCloseBtn">
      <div class="flex flex-col my-4">
        <div class="text-xl">{{ authErrorMessage }}.</div>
        <div class="button-actions mt-8 w-full">
          <VButton class="w-full text-center my-2" variant="copper" @click="goToLoginPage">Log In</VButton>
          <VButton v-if="!hideCloseBtn" class="w-full my-2" @click="authError = false">Dismiss</VButton>
        </div>
      </div>
    </VModal>
    <VModal v-if="permissionsError" v-model:showing="permissionsError" :hide-close="hideCloseBtn">
      <div v-if="!showRequestForm" class="flex flex-col my-4">
        <h2 class="text-2xl mb-4 text-center">YOU DON'T HAVE ACCESS TO THIS PAGE.</h2>
        <p class="mb-6">You don't have permission to view this page. You can request access from the account administrator.</p>
        <VButton class="w-full text-center" variant="copper" @click="showRequestForm = true">Request Access</VButton>
      </div>
      <div v-else class="flex flex-col space-y-6 my-4 w-128">
        <div class="text-center">
          <h2 class="text-2xl mb-2">REQUEST ACCESS</h2>
          <p class="text-sm">Fill out the form to request access to this page</p>
        </div>
        <form class="text-md space-y-4" @submit.prevent="onSubmit">
          <FormField
            v-model="name"
            :v-bind="nameAttrs"
            name="name"
            type="text"
            label="Name"
            placeholder="Enter your name"
            autocomplete="off"
            required
            :error="errors.name"
          />
          <FormField
            v-model="email_address"
            :v-bind="email_addressAttrs"
            name="email_address"
            type="email"
            label="Email Address"
            placeholder="Enter your email address"
            autocomplete="off"
            required
            :error="errors.email_address"
          />
          <FormField
            v-model="reason"
            :v-bind="reasonAttrs"
            name="reason"
            type="text"
            label="Reason"
            placeholder="Enter the reason for your access request"
            autocomplete="off"
            required
            :error="errors.reason"
          />
          <div class="flex gap-x-4 justify-between pt-6">
            <VButton class="w-full text-center" variant="secondary" @click="showRequestForm = false">Back</VButton>
            <VButton class="w-full text-center" variant="copper" @click="onSubmit">Request Access</VButton>
          </div>
        </form>
      </div>
    </VModal>
  </div>
</template>

<script lang="ts" setup>
import { toTypedSchema } from '@vee-validate/zod'
import { useForm } from 'vee-validate'
import z from 'zod'

const props = defineProps({
  // TODO: this needs a better name
  project: {
    type: String,
    required: false,
    default: 'project',
  },

  content: {
    type: String,
    required: false,
    default: 'content',
  },

  showLogin: {
    type: Boolean,
    required: false,
    default: false,
  },

  showSignUp: {
    type: Boolean,
    required: false,
    default: false,
  },

  hasAuthError: {
    type: Boolean,
    required: false,
    default: false,
  },

  hasPermissionsError: {
    type: Boolean,
    required: false,
    default: false,
  },

  shouldRefresh: {
    type: Boolean,
    default: true,
  },

  authErrorCopy: {
    type: String,
    required: false,
    default: '',
  },
  hideClose: {
    type: Boolean,
    default: false,
  },
})

const route = useRoute()

const showRequestForm = ref(false)
const permissionsError = ref(false)
const authError = ref(false)
const { state: userState } = useUser()
const config = useRuntimeConfig()
const { notification, setNotification } = useGlobalNotification()

const emit = defineEmits(['update:hasAuthError'])

const shouldHideClose = computed(() => {
  return permissionsError.value || !userState.value.user
})

const hideCloseBtn = computed(() => {
  return props.hideClose && shouldHideClose.value
})

const authErrorMessage = computed(() => {
  if (props.authErrorCopy && props.authErrorCopy.length > 0) {
    return props.authErrorCopy
  } else {
    return `Please log in to access ${props.project}`
  }
})

watch(
  () => authError.value,
  val => {
    emit('update:hasAuthError', val)
  },
)

watch(
  () => props.showLogin,
  async val => {
    if (val) {
      await navigateTo(
        { path: `${config.public.WEB_ENDPOINT}login`, query: { ...route.query, redirectedFrom: route.path } },
        { external: true },
      )
    }
  },
)

watch(
  () => props.showSignUp,
  async val => {
    if (val) {
      await navigateTo(
        { path: `${config.public.WEB_ENDPOINT}login`, query: { ...route.query, redirectedFrom: route.path } },
        { external: true },
      )
    }
  },
)

watch(
  () => props.hasAuthError,
  val => {
    authError.value = val
  },
  { immediate: true },
)

watch(
  () => props.hasPermissionsError,
  val => {
    permissionsError.value = val
  },
  { immediate: true },
)

const goToLoginPage = async () => {
  await navigateTo(
    { path: `${config.public.WEB_ENDPOINT}login`, query: { ...route.query, redirectedFrom: route.path } },
    { external: true },
  )
}

const formSchema = toTypedSchema(
  z.object({
    name: z.string().min(1, 'Name is required'),
    email_address: z.string().email('Please enter a valid email address').min(1, 'Email is required'),
    reason: z.string().min(1, 'Reason is required'),
  }),
)

const { handleSubmit, defineField, errors } = useForm({
  validationSchema: formSchema,
  initialValues: {
    name: userState.value.user?.name ?? '',
    email_address: userState.value.user?.email_address ?? '',
    reason: '',
  },
})

const [name, nameAttrs] = defineField('name')
const [email_address, email_addressAttrs] = defineField('email_address')
const [reason, reasonAttrs] = defineField('reason')

const onSubmit = handleSubmit(async values => {
  try {
    await requestDeckAccess(route.params.id as string, {
      name: values.name,
      email: values.email_address,
      reason: values.reason,
    })
  } finally {
    setNotification({
      title: 'Access Request Submitted',
      text: 'Your access request has been submitted. You will be notified via email when it is approved.',
    })
    showRequestForm.value = false
  }
})

async function requestDeckAccess(deckId: string | number, payload: { name: string; email: string; reason: string }): Promise<void> {
  return await $fetch<void>(`/v2/decks/${deckId}/request_access`, {
    ...useFetchSharedOptions(),
    method: 'POST',
    body: payload,
  })
}
</script>

<style lang="stylus" scoped>
.modals-container
  z-index: 9000
  position: fixed
</style>
